import { useEffect, useState } from 'react';
import { useNetworkStats } from '@/hooks/useNetworkStats';
import { TrendingUp, FileText, CheckCircle, Clock, XCircle, DollarSign, Lock, ArrowUpRight, RefreshCw, ExternalLink, Globe } from 'lucide-react';
import { MEZO_EXPLORER_URL } from '@/lib/boar-config';
import { MEZO_CONTRACTS } from '@/lib/mezo';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { clearExplorerCache } from '@/services/explorer-api';

function NetworkStatsContent() {
  const { stats, recentActivity, isLoading, error, rpcWarning, explorerOk, rpcOk, refresh, bitcoinPrice, websiteVisitsError, isGlobalTracking } = useNetworkStats();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Force fresh data fetch on page mount - ensure public data consistency
  useEffect(() => {
    console.log('[NetworkStats] Page mounted - forcing fresh data fetch for public consistency');
    // Clear Explorer API cache to ensure fresh data
    clearExplorerCache();
    // Trigger refresh to fetch latest data from blockchain
    refresh();
  }, [refresh]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refresh();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, refresh]);

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-400/30 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-bold font-navbar text-white mb-2">Unable to Load Statistics</h3>
            <p className="text-sm text-white/70 font-navbar mb-6">{error}</p>
            <button
              onClick={refresh}
              className="px-6 py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-400/50 hover:border-green-400/70 rounded-lg text-green-400 font-semibold font-navbar transition-all"
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-chart-line text-white text-2xl sm:text-3xl"></i>
              <h1 className="text-3xl sm:text-4xl font-bold font-navbar text-white">
                Network Statistics
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-navbar ${
                  autoRefresh 
                    ? 'bg-green-500/10 border-green-400/50 text-green-400' 
                    : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                Auto-refresh
              </button>
              <button
                onClick={refresh}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-400/50 text-blue-400 hover:bg-blue-500/20 transition-all text-sm font-navbar"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
          <p className="text-sm sm:text-base font-navbar text-white/70 max-w-2xl">
            Real-time network metrics directly from the blockchain. No login required. 100% transparent and verifiable.
          </p>
        </div>
      </div>

      
      {rpcWarning && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3">
          <p className="text-sm font-navbar text-amber-200/90">
            RPC temporarily unavailable. Showing data from Explorer.
          </p>
          <button
            onClick={refresh}
            className="shrink-0 rounded-lg bg-amber-500/20 px-3 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/30 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center mx-auto mb-6">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
            </div>
            <h3 className="text-xl font-bold font-navbar text-white mb-2">Loading Network Statistics</h3>
            <p className="text-sm text-white/70 font-navbar">Fetching real-time data from blockchain...</p>
          </div>
        </div>
      ) : stats ? (
        <>
          {/* Primary Metrics - Large Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-purple-500/20 via-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-purple-400/30 rounded-2xl p-5 hover:border-purple-400/50 transition-all hover:shadow-lg hover:shadow-purple-500/10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/30 border border-purple-400/50 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-300" />
                </div>
              </div>
              <p className="text-3xl font-bold font-navbar text-white mb-1">{stats.totalInvoices.toLocaleString()}</p>
              <p className="text-sm font-navbar text-purple-300/80 font-semibold mb-1">Total Invoices</p>
            </div>

            <div className="bg-gradient-to-br from-green-500/20 via-green-500/10 to-emerald-500/10 backdrop-blur-xl border border-green-400/30 rounded-2xl p-5 hover:border-green-400/50 transition-all hover:shadow-lg hover:shadow-green-500/10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/30 border border-green-400/50 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-300" />
                </div>
              </div>
              <p className="text-3xl font-bold font-navbar text-white mb-1">{stats.paidInvoices.toLocaleString()}</p>
              <p className="text-sm font-navbar text-green-300/80 font-semibold mb-1">Paid Invoices</p>
              <p className="text-xs font-navbar text-green-400">{stats.paymentSuccessRate}% success rate</p>
            </div>

            <div className="bg-gradient-to-br from-yellow-500/20 via-yellow-500/10 to-orange-500/10 backdrop-blur-xl border border-yellow-400/30 rounded-2xl p-5 hover:border-yellow-400/50 transition-all hover:shadow-lg hover:shadow-yellow-500/10">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/30 border border-yellow-400/50 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-yellow-300" />
                </div>
              </div>
              <p className="text-2xl font-bold font-navbar text-white mb-1">
                ${stats.totalVolumeUSD.toLocaleString(undefined, { maximumFractionDigits: 0, notation: 'compact' })}
              </p>
              <p className="text-sm font-navbar text-yellow-300/80 font-semibold mb-1">Total Volume</p>
              <p className="text-xs font-navbar text-white/50">{stats.totalVolumeBTC.toFixed(4)} BTC</p>
            </div>
          </div>

          {/* Secondary Metrics - Pill Shaped Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-10">
            <div className="bg-orange-500/20 backdrop-blur-xl border border-orange-400/40 rounded-2xl px-4 py-5 flex items-center justify-between hover:bg-orange-500/30 hover:border-orange-400/60 transition-all">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-400" />
                <p className="text-xs font-navbar text-white/80 font-semibold">Pending</p>
              </div>
              <p className="text-xl font-bold font-navbar text-orange-400">{stats.pendingInvoices.toLocaleString()}</p>
            </div>
            <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/40 rounded-2xl px-4 py-5 flex items-center justify-between hover:bg-red-500/30 hover:border-red-400/60 transition-all">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <p className="text-xs font-navbar text-white/80 font-semibold">Cancelled</p>
              </div>
              <p className="text-xl font-bold font-navbar text-red-400">{stats.cancelledInvoices.toLocaleString()}</p>
            </div>
            <div className="bg-green-500/20 backdrop-blur-xl border border-green-400/40 rounded-2xl px-4 py-5 flex items-center justify-between hover:bg-green-500/30 hover:border-green-400/60 transition-all">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <p className="text-xs font-navbar text-white/80 font-semibold">Success Rate</p>
              </div>
              <p className="text-xl font-bold font-navbar text-green-400">{stats.paymentSuccessRate}%</p>
            </div>
            <div className="bg-cyan-500/20 backdrop-blur-xl border border-cyan-400/40 rounded-2xl px-4 py-5 flex items-center justify-between hover:bg-cyan-500/30 hover:border-cyan-400/60 transition-all">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <p className="text-xs font-navbar text-white/80 font-semibold">Website Visits</p>
              </div>
              <p className="text-xl font-bold font-navbar text-cyan-400">{stats.websiteVisits.toLocaleString()}</p>
            </div>
          </div>

          {/* Vault Metrics - 2 in a row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            <div className="bg-gradient-to-br from-green-500/20 via-green-500/10 to-emerald-500/10 backdrop-blur-xl border border-green-400/30 rounded-2xl p-6 hover:border-green-400/50 transition-all hover:shadow-lg hover:shadow-green-500/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/30 border border-green-400/50 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-green-300" />
                </div>
                <div>
                  <h3 className="text-base font-bold font-navbar text-white mb-0.5">Total Value Locked</h3>
                  <p className="text-xs font-navbar text-white/60">BTC in Vaults</p>
                </div>
              </div>
              <p className="text-3xl font-bold font-navbar text-green-400 mb-2">
                {stats.totalTVL.toFixed(4)} BTC
              </p>
              <p className="text-sm font-navbar text-white/70">
                ≈ ${(stats.totalTVL * bitcoinPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })} USD
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-indigo-500/10 backdrop-blur-xl border border-blue-400/30 rounded-2xl p-6 hover:border-blue-400/50 transition-all hover:shadow-lg hover:shadow-blue-500/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/30 border border-blue-400/50 flex items-center justify-center">
                  <ArrowUpRight className="w-6 h-6 text-blue-300" />
                </div>
                <div>
                  <h3 className="text-base font-bold font-navbar text-white mb-0.5">Total Borrowed</h3>
                  <p className="text-xs font-navbar text-white/60">MUSD Borrowed</p>
                </div>
              </div>
              <p className="text-3xl font-bold font-navbar text-blue-400 mb-2">
                ${stats.totalBorrowed.toLocaleString(undefined, { maximumFractionDigits: 0 })} MUSD
              </p>
              <p className="text-sm font-navbar text-white/70">
                {stats.activeVaults} active vault{stats.activeVaults !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Recent Activity Section */}
          {recentActivity && recentActivity.length > 0 && (
            <div className="mb-10">
              <div className="mb-6">
                <h2 className="text-xl font-bold font-navbar text-white">Recent Activity</h2>
              </div>
              
              <div className="bg-[#2C2C2E]/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {recentActivity.slice(0, 20).map((activity, index) => (
                    <ActivityItem 
                      key={`${activity.invoiceId}-${index}`}
                      activity={activity} 
                      formatTimeAgo={(ts: number) => {
                        const seconds = Math.floor((Date.now() - ts) / 1000);
                        if (seconds < 60) return 'just now';
                        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
                        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
                        return `${Math.floor(seconds / 86400)}d ago`;
                      }}
                    />
                  ))}
                </div>
                
                {recentActivity.length > 20 && (
                  <div className="mt-4 pt-4 border-t border-white/10 text-center">
                    <p className="text-xs font-navbar text-white/60">
                      Showing latest 20 of {recentActivity.length} activities
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer Info */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-[#2C2C2E]/40 backdrop-blur-xl rounded-2xl border border-white/10">
              <div className="flex flex-wrap items-center gap-4 text-xs font-navbar text-white/60">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Last updated: {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleTimeString() : 'Never'}</span>
                </div>
                <span className="hidden sm:inline">•</span>
                <span>100% On-chain Verified</span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:inline">Public & Transparent</span>
              </div>
              <a
                href={`${MEZO_EXPLORER_URL}/address/${MEZO_CONTRACTS.INVOICE_CONTRACT}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-xs font-semibold font-navbar text-green-400 hover:text-green-300 bg-green-500/10 hover:bg-green-500/20 border border-green-400/30 hover:border-green-400/50 rounded-lg transition-all"
              >
                <span>View Smart Contract</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 mt-8">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-lg font-bold font-navbar text-white mb-2">No Data Yet</h3>
            <p className="text-sm font-navbar text-white/60 mb-4">
              Network statistics will appear here once invoices are created on the platform.
            </p>
            <p className="text-xs font-navbar text-white/40">
              All data is fetched directly from the smart contract - no wallet connection required.
            </p>
            <a
              href={`${MEZO_EXPLORER_URL}/address/${MEZO_CONTRACTS.INVOICE_CONTRACT}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 px-4 py-2 bg-green-500/10 border border-green-400/50 rounded-lg text-green-400 hover:bg-green-500/20 transition-colors text-sm font-navbar"
            >
              View Smart Contract
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// Export wrapped component with error boundary
export function NetworkStats() {
  return (
    <ErrorBoundary>
      <NetworkStatsContent />
    </ErrorBoundary>
  );
}

// Activity Item Component - Enhanced Version
function ActivityItem({ activity, formatTimeAgo }: { activity: NetworkActivity; formatTimeAgo: (ts: number) => string }) {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'created':
        return <FileText className="w-4 h-4 text-purple-400" />;
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-400" />;
    }
  };

  const getActivityText = () => {
    switch (activity.type) {
      case 'created':
        return 'Invoice created';
      case 'paid':
        return `Invoice paid${activity.amount ? ` • ${activity.amount.toFixed(4)} BTC` : ''}`;
      case 'cancelled':
        return 'Invoice cancelled';
    }
  };

  const getActivityColor = () => {
    switch (activity.type) {
      case 'created':
        return 'border-purple-400/30 bg-gradient-to-r from-purple-500/10 to-purple-400/5 hover:from-purple-500/15 hover:to-purple-400/10';
      case 'paid':
        return 'border-green-400/30 bg-gradient-to-r from-green-500/10 to-emerald-400/5 hover:from-green-500/15 hover:to-emerald-400/10';
      case 'cancelled':
        return 'border-red-400/30 bg-gradient-to-r from-red-500/10 to-pink-400/5 hover:from-red-500/15 hover:to-pink-400/10';
    }
  };

  return (
    <div className={`group flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 hover:scale-[1.01] hover:shadow-lg ${getActivityColor()}`}>
      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
        {getActivityIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold font-navbar text-white truncate mb-0.5">
          {getActivityText()}
        </p>
        <div className="flex items-center gap-2">
          <p className="text-xs font-navbar text-white/60">
            {formatTimeAgo(activity.timestamp)}
          </p>
          {activity.user && (
            <>
              <span className="text-xs text-white/40">•</span>
              <span className="text-xs font-mono font-navbar text-white/50 truncate max-w-[100px]" title={activity.user}>
                {activity.user}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
